import { MuiThemeProvider, StylesProvider } from '@material-ui/core/styles';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { connect, Provider } from 'react-redux';

import { setImageUploadConfig } from 'react-text-editor';

import { fetchSession, requestToasterOpen, showModalAlert } from './actions';
import { initializeMixpanel } from './analytics';
import { authPopup, ensureAuth } from './auth';
import { deleteExpiredDrafts } from './common/draft-storage';
import { getLocalStorageKey, pruneExpiredKeys } from './common/local-storage';
import { logger } from './common/logger';
import { store } from './configure-store';
import { Loading } from './core-ui/loading';
import { ReduxContext } from './hooks/redux-context';
import { useReduxDispatch } from './hooks/use-redux';
import { SessionProvider } from './hooks/use-session';
import { handleImageUpload } from './lib/image-upload';
import { muiTheme } from './mui-theme';
import { AppConstantState, Session, State } from './state';

interface OwnProps {
    component: JSX.Element;
}

interface ConnectedProps {
    appConstants: AppConstantState;
    session: Session;
}

type AppProps = OwnProps & ConnectedProps;

const AppComponent: React.FC<AppProps> = (props: AppProps) => {
    const { session, component, appConstants } = props;
    const dispatch = useReduxDispatch();

    React.useEffect(() => {
        if (session.initialized && appConstants.initialized) {
            setImageUploadConfig({
                onError: (error) => {
                    dispatch(showModalAlert('Image upload failed: ' + error?.message ?? 'Unknown error', 'Error'));
                },
                onUpload: handleImageUpload
            });
        }
    }, [session, appConstants]);

    if (!session.initialized || !appConstants.initialized) {
        const loadingSize = 60;
        const style = {
            height: '100vh',
            width: '100vw'
        };
        return (
            <div style={style}>
                <Loading size={loadingSize} />
            </div>
        );
    } else {
        pruneExpiredKeys();
        deleteExpiredDrafts().catch((err) => {
            logger.warn(err, { message: 'error pruning expired drafts' });
        });
        return component;
    }
};

const mapStateToProps = (state: State): ConnectedProps => ({
    appConstants: state.appConstants,
    session: state.session
});
const App = connect<ConnectedProps, undefined, OwnProps>(mapStateToProps)(AppComponent);

const unhandledErrorMessage = 'Something went wrong. The error has been reported.';
const unhandledErrorToastClass = 'toast-error';

// Errors thrown in event handlers and rejected promises never reach the React error boundary,
// so without this the user sees nothing happen.
const registerUnhandledErrorToasts = () => {
    window.addEventListener('error', (event) => {
        // Cross-origin script errors and ResizeObserver notices carry no error object
        if (!event.error) {
            return;
        }
        // The error fires inside the click that caused it; opening the toast synchronously makes
        // that same click count as a click-away and close it
        window.setTimeout(() =>
            store.dispatch(requestToasterOpen(unhandledErrorMessage, undefined, unhandledErrorToastClass))
        );
    });
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        if (reason?.name === 'AbortError') {
            return;
        }
        // Bugsnag picks up uncaught exceptions on its own but not rejected promises
        logger.error(reason instanceof Error ? reason : new Error(String(reason)));
        store.dispatch(requestToasterOpen(unhandledErrorMessage, undefined, unhandledErrorToastClass));
    });
};

export function init(authUXMode: 'redirect' | 'popup', component: JSX.Element, waitForDocumentVisible = false) {
    initializeMixpanel();
    registerUnhandledErrorToasts();

    let root: Root | null = null;
    const getRoot = () => {
        if (root) {
            return root;
        }
        const container = document.getElementById('app-root');
        if (!container) {
            throw new Error('App root element not found');
        }
        root = createRoot(container);
        return root;
    };

    const renderDomContent = () => {
        getRoot().render(
            <Provider store={store}>
                <ReduxContext.Provider value={store}>
                    <SessionProvider>
                        <MuiThemeProvider theme={muiTheme}>
                            <StylesProvider injectFirst={true}>
                                <App component={component} />
                            </StylesProvider>
                        </MuiThemeProvider>
                    </SessionProvider>
                </ReduxContext.Provider>
            </Provider>
        );
    };

    const renderDom = () => {
        if (!waitForDocumentVisible || !document.hidden) {
            renderDomContent();
        } else {
            const handleVisibilityChange = () => {
                if (!document.hidden) {
                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                    renderDomContent();
                }
            };
            document.addEventListener('visibilitychange', handleVisibilityChange, false);
        }
    };

    const authSuccessHandler = (token: string) => {
        store.dispatch(fetchSession(token, undefined));
        renderDom();
    };

    const useSavedSessionAuth = (token: string) => {
        store.dispatch(fetchSession(undefined, token));
        renderDom();
    };

    const runPopupLogin = () => {
        authPopup(() => window.location.reload(), authErrorHandler);
    };

    const authErrorHandler = () => {
        getRoot().render(
            <div className="extension-login-container">
                <button className="login-button" onClick={runPopupLogin}>
                    Sign In
                </button>
            </div>
        );
    };

    // check session from local storage
    const session = getLocalStorageKey('session', undefined);
    if (session) {
        useSavedSessionAuth(session.authToken);
        // initialize google auth lib in either case
        ensureAuth(
            authUXMode,
            () => {
                /* */
            },
            () => {
                /** */
            }
        );
    } else {
        ensureAuth(authUXMode, authSuccessHandler, authErrorHandler);
    }
}
